import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Tag, Typography, Space, Spin, Empty, Image, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import { PageHeader } from 'components/headings';
import { getReporteVisita } from 'api/reporteVisita';
import { getSignatureUrl } from 'api/signatures';
import Dowload from 'PDF/ReporteVisita/Dowload';

const { Paragraph, Text } = Typography;

const TIPO_LABEL = {
  levantamiento: 'Levantamiento',
  diagnostico: 'Diagnóstico',
  preinspeccion: 'Preinspección',
  cierre: 'Cierre',
  libre: 'Libre',
};

const ESTADO_COLOR = { OK: 'green', FALLA: 'red', NA: 'default' };
const ESTADO_TXT = { OK: 'OK', FALLA: 'Falla', NA: 'N/A' };

const fmt = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—');
const gps = (lat, lng) =>
  lat != null && lng != null
    ? `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`
    : 'No registrada';

function FirmaPreview({ blockKey }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (blockKey) {
      getSignatureUrl(blockKey)
        .then((u) => !cancelled && setUrl(u))
        .catch(() => !cancelled && setUrl(null));
    }
    return () => {
      cancelled = true;
    };
  }, [blockKey]);
  if (!blockKey) return <Text type="secondary">Sin firma</Text>;
  if (!url) return <Spin size="small" />;
  return <Image src={url} alt="Firma" width={200} preview={false} />;
}

function BlockView({ block }) {
  if (block.type === 'text') {
    return <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{block.config?.body || '—'}</Paragraph>;
  }
  if (block.type === 'checklist') {
    const items = block.config?.items || [];
    if (items.length === 0) return <Text type="secondary">Sin ítems</Text>;
    return (
      <Space direction="vertical" style={{ width: '100%' }} size={6}>
        {items.map((it, i) => (
          <Space key={i} align="start">
            <Tag color={ESTADO_COLOR[it.estado] || 'default'}>
              {ESTADO_TXT[it.estado] || it.estado}
            </Tag>
            <div>
              <div>{it.label || '—'}</div>
              {it.nota ? <Text type="secondary">{it.nota}</Text> : null}
            </div>
          </Space>
        ))}
      </Space>
    );
  }
  if (block.type === 'signatures') {
    return <FirmaPreview blockKey={block.config?.specialistSignatureKey} />;
  }
  return <Text type="secondary">Bloque: {block.type}</Text>;
}

export default function ReporteVisitaViewer() {
  const { id } = useParams();
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDownload, setShowDownload] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getReporteVisita(Number(id));
        if (!cancelled) setReporte(r || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }
  if (!reporte) return <Empty description="Reporte no encontrado" />;

  const clienteLabel =
    reporte.cliente_no_registrado?.nombre ||
    (reporte.id_cliente ? `Cliente #${reporte.id_cliente}` : '—');
  const secciones = Array.isArray(reporte.secciones) ? reporte.secciones : [];

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: 12 }}>
      <PageHeader
        title={reporte.titulo}
        subtitle="Reporte de visita en terreno"
        actions={
          showDownload ? (
            <Dowload reporte={reporte} clienteNombre={clienteLabel} config={{}} />
          ) : (
            <Button icon={<DownloadOutlined />} onClick={() => setShowDownload(true)}>
              Descargar PDF
            </Button>
          )
        }
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Cliente">{clienteLabel}</Descriptions.Item>
          <Descriptions.Item label="Tipo">
            {TIPO_LABEL[reporte.tipo] || reporte.tipo}
          </Descriptions.Item>
          <Descriptions.Item label="Estado">
            <Tag color={reporte.estado === 'draft' ? 'gold' : 'green'}>
              {reporte.estado === 'draft' ? 'Borrador' : 'Cerrada'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Técnico">{reporte.created_by_rut}</Descriptions.Item>
          <Descriptions.Item label="Apertura">{fmt(reporte.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Cierre">{fmt(reporte.finalizado_at)}</Descriptions.Item>
          <Descriptions.Item label="Ubicación apertura">
            {gps(reporte.gps_apertura_lat, reporte.gps_apertura_lng)}
          </Descriptions.Item>
          <Descriptions.Item label="Ubicación cierre">
            {gps(reporte.gps_cierre_lat, reporte.gps_cierre_lng)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {secciones.length === 0 ? <Empty description="Sin bloques" /> : null}
        {secciones.map((block) => (
          <Card key={block.id} size="small" title={block.title || block.type}>
            <BlockView block={block} />
          </Card>
        ))}
      </Space>
    </div>
  );
}
